/**
 * Catchball Ball Store
 *
 * First-class Ball objects inside a Catchball Loop.
 * One Loop → many Balls (one per participating org branch).
 * Each Ball carries a Request downward and a Proposal upward.
 *
 * Invariants (Canon: docs/canon-addendum-ball-tree-split-aggregate.md):
 *   - One Proposal per Ball per Loop (not per Track per Loop)
 *   - Ball belongs to exactly one Loop
 *   - Ball status transitions are append-only (no back-transitions except returned_for_revision)
 *   - DB-first: no localStorage. All state from Supabase.
 */

import { useMemo } from 'react';
import { create } from 'zustand';
import { getSafeSupabaseClient, isSupabaseAvailable } from '../../lib/supabaseSync';
import {
  type CatchballBall,
  type BallStatus,
  rowToCatchballBall,
} from '../../types/prioritization/catchballBall';

// ── Store shape ───────────────────────────────────────────────────────────────

interface CatchballBallState {
  /** All fetched balls, keyed by id for fast lookup */
  ballsById: Record<string, CatchballBall>;
  /** Ordered ball ids per loopId */
  ballIdsByLoop: Record<string, string[]>;
  isLoading: boolean;
  error: string | null;

  // Queries
  getBallsForLoop: (loopId: string) => CatchballBall[];
  getBallsForCycle: (cycleId: string) => CatchballBall[];
  getBall: (ballId: string) => CatchballBall | undefined;
  /** Direct children of a parent ball (Rollout siblings). */
  getChildBalls: (parentBallId: string) => CatchballBall[];
  /** Root ball for a loop (parent_ball_id IS NULL), if any. */
  getRootBall: (loopId: string) => CatchballBall | undefined;

  // Actions
  fetchBallsForLoop: (loopId: string) => Promise<void>;
  fetchBallsForCycle: (cycleId: string) => Promise<void>;
  /**
   * Move a Ball one step forward along the recipient's part of its lifecycle
   * (sent → opened → gathering_input → in_local_deliberation →
   * proposal_drafted). §2.3a: one server transaction
   * (catchball_advance_ball_status, migration 772), which checks that the
   * caller is the ball's recipient, someone in its recipient org unit, its
   * creator or a workspace admin. Balls are created by catchball_send_the_ball
   * / _fan_out / _spawn_child_ball and closed by the decision RPCs — there is
   * no client create, edit or delete.
   */
  advanceBallStatus: (ballId: string, newStatus: BallStatus) => Promise<CatchballBall | null>;

  // Internal
  _upsertBalls: (balls: CatchballBall[]) => void;
  _removeBall: (ballId: string) => void;
}

// ── Store ─────────────────────────────────────────────────────────────────────

/**
 * Stable empty lists for unloaded loops — same pattern as
 * `EMPTY_ACTIVE_POSITIONS` in catchballTierTurnStore.
 *
 * Calling `getBallsForLoop` / `getChildBalls` *inside* a Zustand selector
 * allocates a new array every read → React `useSyncExternalStore` infinite
 * loop (breaks §2.4 proof-of-life). Use `useBallsForLoop` in components, or
 * select `ballIdsByLoop` / `ballsById` and derive with `useMemo`.
 */
const EMPTY_BALLS: CatchballBall[] = [];
const EMPTY_BALL_IDS: string[] = [];

export const useCatchballBallStore = create<CatchballBallState>((set, get) => ({
  ballsById: {},
  ballIdsByLoop: {},
  isLoading: false,
  error: null,

  // ── Queries ────────────────────────────────────────────────────────────────

  getBallsForLoop: (loopId) => {
    const ids = get().ballIdsByLoop[loopId] ?? EMPTY_BALL_IDS;
    if (ids.length === 0) return EMPTY_BALLS;
    const byId = get().ballsById;
    return ids.map((id) => byId[id]).filter(Boolean).sort(
      (a, b) => a.sequenceInLoop - b.sequenceInLoop
    );
  },

  getBallsForCycle: (cycleId) => {
    return Object.values(get().ballsById).filter((b) => b.cycleId === cycleId).sort(
      (a, b) => a.sequenceInLoop - b.sequenceInLoop
    );
  },

  getBall: (ballId) => get().ballsById[ballId],

  getChildBalls: (parentBallId) => {
    const kids = Object.values(get().ballsById)
      .filter((b) => b.parentBallId === parentBallId)
      .sort((a, b) => a.sequenceInLoop - b.sequenceInLoop);
    return kids.length === 0 ? EMPTY_BALLS : kids;
  },

  getRootBall: (loopId) =>
    get()
      .getBallsForLoop(loopId)
      .find((b) => b.parentBallId == null),

  // ── Internal upsert ────────────────────────────────────────────────────────

  _upsertBalls: (incoming) => {
    set((state) => {
      const byId = { ...state.ballsById };
      const byLoop = { ...state.ballIdsByLoop };

      for (const ball of incoming) {
        byId[ball.id] = ball;
        const ids = byLoop[ball.loopId] ?? [];
        if (!ids.includes(ball.id)) byLoop[ball.loopId] = [...ids, ball.id];
      }

      return { ballsById: byId, ballIdsByLoop: byLoop };
    });
  },

  _removeBall: (ballId) => {
    set((state) => {
      const byId = { ...state.ballsById };
      const removed = byId[ballId];
      delete byId[ballId];

      const byLoop = { ...state.ballIdsByLoop };
      if (removed) {
        byLoop[removed.loopId] = (byLoop[removed.loopId] ?? []).filter((id) => id !== ballId);
      }

      return { ballsById: byId, ballIdsByLoop: byLoop };
    });
  },

  // ── fetchBallsForLoop ──────────────────────────────────────────────────────

  fetchBallsForLoop: async (loopId) => {
    if (!isSupabaseAvailable()) return;
    set({ isLoading: true, error: null });
    try {
      const supabase = getSafeSupabaseClient();
      const { data, error } = await supabase
        .from('catchball_balls_v')
        .select('*')
        .eq('loop_id', loopId)
        .order('sequence_in_loop', { ascending: true });

      if (error) throw error;
      if (data) get()._upsertBalls(data.map(rowToCatchballBall));
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to fetch balls';
      set({ error: msg });
      console.error('[catchballBallStore] fetchBallsForLoop', err);
    } finally {
      set({ isLoading: false });
    }
  },

  // ── fetchBallsForCycle ─────────────────────────────────────────────────────

  fetchBallsForCycle: async (cycleId) => {
    if (!isSupabaseAvailable()) return;
    set({ isLoading: true, error: null });
    try {
      const supabase = getSafeSupabaseClient();
      const { data, error } = await supabase
        .from('catchball_balls_v')
        .select('*')
        .eq('cycle_id', cycleId)
        .order('sequence_in_loop', { ascending: true });

      if (error) throw error;
      if (data) get()._upsertBalls(data.map(rowToCatchballBall));
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to fetch balls for cycle';
      set({ error: msg });
      console.error('[catchballBallStore] fetchBallsForCycle', err);
    } finally {
      set({ isLoading: false });
    }
  },

  // ── advanceBallStatus ──────────────────────────────────────────────────────

  advanceBallStatus: async (ballId, newStatus) => {
    try {
      const supabase = getSafeSupabaseClient();
      if (!supabase) return null;
      const { data, error } = await supabase.rpc('catchball_advance_ball_status', {
        p_ball_id: ballId,
        p_status: newStatus,
      });
      if (error) throw error;
      const result = (data ?? null) as { success?: boolean; error?: string; ball?: Record<string, unknown> } | null;
      if (!result?.success || !result.ball) {
        set({ error: result?.error || 'The server refused the Ball.' });
        return null;
      }
      const ball = rowToCatchballBall(result.ball);
      get()._upsertBalls([ball]);
      set({ error: null });
      return ball;
    } catch (err) {
      console.error('[catchballBallStore] advanceBallStatus', err);
      set({ error: (err as { message?: string } | null)?.message || 'The server refused the Ball.' });
      return null;
    }
  },
}));

/**
 * React-safe balls-for-loop subscription.
 * Prefer this over `useCatchballBallStore(s => s.getBallsForLoop(id))`.
 */
export function useBallsForLoop(loopId: string | null | undefined): CatchballBall[] {
  const ids = useCatchballBallStore((s) =>
    loopId ? (s.ballIdsByLoop[loopId] ?? EMPTY_BALL_IDS) : EMPTY_BALL_IDS,
  );
  const ballsById = useCatchballBallStore((s) => s.ballsById);
  return useMemo(() => {
    if (ids.length === 0) return EMPTY_BALLS;
    return ids
      .map((id) => ballsById[id])
      .filter(Boolean)
      .sort((a, b) => a.sequenceInLoop - b.sequenceInLoop);
  }, [ids, ballsById]);
}
